import c3 from 'c3';


import { Subject, from } from 'rxjs';
import * as moment from 'moment-timezone';
import merge from 'lodash-es/merge';
import isArray from 'lodash-es/isArray';
import difference from 'lodash-es/difference';

import {
	Component, ElementRef, Input, ViewChild, EventEmitter, Output,
} from "@angular/core";
import {EventsService} from "../../lib/EventsService";
import {PrettyNumberPipe} from "../../pipes/pretty-number.pipe";

const DATE_PERIOD_LIST = [ 'hour', 'day' ];
const DATE_FILTER_LIST = [ 'last30day', 'all' ];


interface ChartFilter {
	period?: string;
	filter?: string;
}

@Component({
	selector: 'chart-filter',
	template: `
		<div class="btn-group" role="group" *ngIf="filter.datePeriodList.length || filter.dateFilterList.length">
			<ng-container *ngFor="let _period of filter.datePeriodList">
				<button
					type="button"
					[class]="period == _period ? 'btn btn-light active' : 'btn btn-light'"
					(click)="_onFilterChange({ period: _period })"
				>
					{{ _period }}
				</button>
			</ng-container>
			
			<ng-container *ngFor="let filter of filter.dateFilterList">
				<button
					type="button"
					[class]="dateFilterType == filter ? 'btn btn-light active' : 'btn btn-light'"
					(click)="_onFilterChange({ filter: filter })"
				>
					{{ filter }}
				</button>
			</ng-container>
		</div>
	`,
	providers: []
})
export class ChartFilterComponent {
	@Input() public period:string      = 'day';
	@Input() public filter:Filter      = new Filter();
	
	public dateFilterType:string = 'last30day';
	
	@Output() public onFilterChange: EventEmitter<Object> = new EventEmitter<Object>();
	
	constructor(public eventsService: EventsService) {
		const me = this;
		
		me.eventsService.on('period-change', function (period) {
			if (period === 'day') {
				me.dateFilterType = 'last30day';
			} else {
				me.dateFilterType = 'all';
			}
		})
	}
	
	_onFilterChange(filter: ChartFilter) {
		if (filter.period) {
			this.period = filter.period;
		} else if (filter.filter) {
			this.dateFilterType = filter.filter;
		}
		
		this.onFilterChange.emit({
			period      : this.period,
			chartFilter : {
				type: this.dateFilterType
			}
		});
	}
}

export class Filter {
	datePeriodList = DATE_PERIOD_LIST;
	dateFilterList = DATE_FILTER_LIST;
}

const chartSub = new Subject();
const chartEvent = from(chartSub);

export class Options {
	public isLoad: boolean = false;
	public isChartFilter?: boolean = false;
	
	title: string = '';
	
	showLabels? = [];
	labels? = {};
	colors? = {};
	axis? = {};
	grid? = {};
	period? = '';
	type?;
	axes? = {};
	names? = {};
	filter = new Filter();
	chartType = 'line';
	chartValue? = '';
	onclick?;
	tooltip?;
	groups = [];
	events? = {};
	size? = {height: 320};
	padding? = '';
	legend? = {};
	
	toggle = chartEvent;
	$toggle = chartSub;

	lazyRender = false;
}

export class ChartParams {
	public name: string = '';
	
	public data = {};
	
	public options = new Options();
	
	constructor(opts?: any) {
		if (opts) this.setOptions(opts);
	}
	
	setOptions(options) {
		this.options = merge({}, this.options, options);
		
		if (options && options.filter && options.filter.datePeriodList) {
			this.options.filter.datePeriodList = options.filter.datePeriodList;
		}
	}
	
	getOptions() {
		return merge({}, this.options);
	}
	
	public filter: any = {};
	
	get isLoader() {
		return this.options.isLoad;
	}
	
	set title(title) {
		this.options.title = title;
	}
	
	showLoader() {
		this.options.isLoad = true;
	}
	
	hideLoader() {
		this.options.isLoad = false;
	}
	
	getFilterParams(filterParams) {
		return merge({}, filterParams, this.filter);
	}
}

function downloadChartData(anchor, data, options) {
	let isCats = (options.axis ? (options.axis.x ? (options.axis.x.categories ? true: false) : false)  : false);
	let titles = [];
	let rows = [];
	let kk = Object.keys(data);
	for (let i of kk) {
		let title = data[i][0];
		
		if (options && options['names'] && options['names'][title]) {
			title = `"${options['names'][title]}"`;
		}
		titles.push(title);
		
		let row = (data[i]).slice(1);
		rows.push(row);
	}
	
	if(isCats) {
		titles.unshift('*');
	}
	
	let csv = titles.join(',') + '\n';
	
	for (let i = 1; i < data[0].length; i++) {
		let line = [];
		if(isCats) {
			let cat = (options.axis.x.categories[i - 1]).toString();
			cat = cat.replace(/, /g, '/');
			cat = cat.replace(/,/g, '/');
			line.push(cat);
		}
		csv += kk.reduce(function (p, c) {
			let v = data[c][i];
			if (c == '0' && isFinite(v) && v > 946674000000) {
				v = moment(v).format('DD.MM.YYYY');
			}
			p.push(v);
			return p;
		}, line).join(',') + '\n';
	}

	anchor.download = 'chart_data.csv';
	anchor.href = "data:text/csv;charset=utf-8;base64," + window.btoa(unescape(encodeURIComponent(csv)));
	anchor.click();
}

@Component({
	selector: 'chart',
	template: `
		<div class="row">
			<div class="col">
				<h3
					*ngIf="options.title"
					class="chart-title-aligment"
				>
					{{ options.title }}
				</h3>
			</div>
			
			<div
				class="col-md-auto"
				*ngIf="isHasData"
			>
				<button
					type="button"
					class="btn btn-outline-success btn-sm"
					[sextant]="'chart-download'"
					(click)="exportData()"
				>
					<i class="fa fa-file-excel" aria-hidden="true">
						<a href="#" #exportFile></a>
					</i>
				</button>
			</div>
		</div>
		
		<chart-filter
			*ngIf="options.isChartFilter"
			[period]="period"
			[filter]="options.filter"
			(onFilterChange)="_onFilterChange($event)"
		></chart-filter>

		<div>
			<div #line [ngStyle]="{display: (options.isLoad ? 'none': '')}"></div>
			<i class="fas fa-volume-mute" *ngIf="isHasData" (click)="hideAllLabels()">&nbsp;</i>
		</div>

		<div
			class="d-flex justify-content-center"
			[ngStyle]="getStyle()"
		>
			<div class="align-self-center">
				<loading
					[isLoad]="options.isLoad"
				></loading>
				
				<span
					*ngIf="! (options.isLoad || isHasData)"
				>
					Ещё нет данных!
				</span>
			</div>
		</div>
	`,
	styles: [`
		:host >>> i.fa-volume-mute {
			position: absolute;
			margin-top: -20px;
			margin-left: 20px;
			color: #AAAAAA;
			cursor: pointer;
		}

		:host >>> i.fa-volume-mute:hover {
			color: #ECF2F7;
		}
	`],
	providers: [PrettyNumberPipe]
})
export class ChartComponent {
	public chart: Line2;
	public period: string = 'day';

	public flag: boolean = false;
	
	// TODO заменить на массив и проверить, что ничего не сломается
	@Input() public data = {};
	@Input() public options = new Options();
	@Input() public name: string = 'chart1';
	
	@Output() public onFilterChange: EventEmitter<Object> = new EventEmitter<Object>();

	@ViewChild('line', { static: true }) public line: ElementRef;
	
	// Тут static === false
	// Иначе падает при клике на выгрузку данных с графика
	// TODO Разобраться что это вообще за опция
	@ViewChild('exportFile', { static: false }) exportFile: ElementRef;

	constructor(public prettyNumber: PrettyNumberPipe) {
		this.options.toggle.subscribe(label => {
			this.toggle(label);
		});
	}

	getStyle() {
		if (this.options.isLoad || !this.isHasData) {
			return {
				'height': `${this.options.size.height}px`,
				'display': 'flex',
				'background-color': '#ECF2F7'
			}
		} else {
			return {};
		}
	}
	
	getChartInstance(data, options: Options): Line2 {
		const me = this;
		
		// Преобразовать параметры в c3 конфиг
		const opts = merge(
			{},
			{
				showLabels: options.showLabels || [],
				groups: options.groups,
				
				size: options.size,
				padding: options.padding,
				
				bindto: me.line.nativeElement,
			},
			{
				axis: options.axis || {},
				grid: options.grid || {},
				
				data: {
					names: merge({}, options.names),
					colors: merge({}, options.colors),
					axes: merge({}, options.axes),
					type: options.type,
					onclick: options.onclick
				},
				
				period: this.period,
				
				legend: options.legend || {},
				
				tooltip: options.tooltip || {
					format: {
						value: value => {
							return me.prettyNumber.transform(value)
						}
					}
				},
				groups: options.groups,
				chartType: options.chartType
			}
		);
		
		
		if (options.events && Object.keys(options.events).length > 0) {
			Object.keys(options.events).forEach(function (event) {
				opts.data[event] = options.events[event];
			});
		}

		return new Line2(data, opts, options.lazyRender);
	}
	
	ngOnChanges() {
		const me = this;
		
		if (me.chart) {
			me.chart = me.chart.destroy();
		}

		if (me.isHasData) {
			me.chart = me.getChartInstance(me.data, me.options);
		}
	}
	
	ngOnDestroy() {
		if (this.chart) this.chart.destroy();
	}
	
	_onFilterChange(filter) {
		this.onFilterChange.emit(filter);
	}
	
	toggle(label) {
		this.chart.instance.toggle([label]);
	}

	hideAllLabels() {
		this.chart.instance.hide();
	}
	
	exportData() {
		try {
			downloadChartData(this.exportFile.nativeElement, this.data, this.options);
		} catch (err) {
			alert(err);
		}
	}

	get isHasData() {
		const me = this;
		
		return me.data && ((Array.isArray(me.data) && me.data.length > 0) || (Object.keys(me.data).length > 0))
	}
}

export class Line2 {
	data: any;
	_data: any;
	params: any;
	isRendered: boolean;
	
	instance: any;
	
	constructor( _data: any, params: any, lazyRender: boolean) {
		const me = this;
		
		me.data = merge([], _data);
		me.params = merge(
			{
				keysMap: me.data.map(c => c[0]),
				period: 'day',
				filter: {
					period: 'all'
				},
				showLabels: [],
				
				trendLineKey: '',
				groups: []
			},
			params
		);
		
		try {
			me._data = me.data;
			let weekends = [];
			if (me._data && me._data.length > 0) {
				let x = me._data[0];
				
				x = x.slice(1)
					.map(x => parseInt(x, 10))
					.sort((a, b) => a - b);
				
				let holidays = {};
				
				x.forEach(function (timestamp) {
					const date = new Date(timestamp);
					const day = date.getDay();

					if (day == 6 || day == 0) holidays[date.toString()] = 1;
				});
				
				
				let holidays2 = Object.keys(holidays).reduce(
					function (arr, date) {
						arr.push(new Date(date));
						return arr;
					},
					[]
				);
				
				holidays2.forEach(function (day) {
					weekends.push({
						start: day,
						end: new Date(day.valueOf() + 86400000)
					});
				});
			}
			
			params = merge(
				{
					regions: weekends,
					data: {
						//x: 'x',
						type: params.chartType,
						columns: me.data
					},
					grid: {
						x: {
							show: true
						},
						y: {
							show: true,
							ticks: 6,
							min: -10
						}
					},
					legend: {
						position: 'top'
					},
					axis: {
						x: {
							type: 'timeseries',
							tick: {
								fit: true,
								culling: {
									max: 16
								},
								//format: me.params.period === 'hour' ? '%d-%m-%Y %H:%M' : '%d-%m-%Y'
								format: function (x) {
									if(this.config.axis_x_type === 'timeseries') {
										if (me.params.period === 'hour') {
											return moment(x).tz('Europe/Moscow').format('DD-MM-YYYY HH:mm');
										}
										return moment(x).tz('Europe/Moscow').format('DD-MM-YYYY');
									} else {
										if(this.config.axis_x_categories[x]) {
											return this.config.axis_x_categories[x];
										} else {
											//console.warn('axis_x_categories not found');
											
											return x;
										}
									}
								}
							}
						},
						y: {
							min: 0,
							ticks: 10,
							padding: {
								top: 0,
								bottom: 10
							}
						}
					}
				},
				params
			);
			
			if (params.axis.x.type === 'timeseries' || params.axis.x.type === 'indexed') {
				params.data.x = 'x';
			}
			
			if(params.data.type === 'pie') {
				params.axis = {};
				delete params.data.x;
			}
			
			if (params.data.type === 'bar') {
				params.axis.y.padding.bottom = 0;
			}

			if(lazyRender) {
				let lazyData = [];
				params.data.columns = params.data.columns.reduce((p, c) => {
					if (c[0] == 'x') {
						p.push(c);
					} else {
						lazyData.push(c);
					}

					return p;
				}, []);

				params.data.columns.push(lazyData.shift());

				me.instance = c3.generate(params);

				let timerId = setInterval(function () {
					let columns = [];

					columns.push(lazyData.shift());

					if (me.instance.internal.config == null) {
						clearInterval(timerId);

						return;
					}

					me.instance.load({
						columns: columns
					});

					if (lazyData.length === 0) {
						clearInterval(timerId);
					}
				}, 500);
			} else {
				me.instance = c3.generate(params);
			}
			
			if (params.groups.length) {
				me.instance.groups([
					me.params.groups
				]);
			}
			
			if (me.params.showLabels.length > 0) {
				me.instance.toggle(
					difference(
						me.data.map(c => c[0]),
						me.params.showLabels
					)
				);
			}
		} catch (err) {
			console.error('Chart error', err);
		}
	}
	
	shown() {
		return this.instance.data.shown.apply(this, arguments);
	}
	
	names() {
		return this.instance.data.names.apply(this, arguments);
	}
	
	destroy() {
		if (this.instance) {
			this.instance.destroy.apply(this, arguments);
		}
		
		return this;
	}
	
	unload() {
		this.instance.unload.apply(this, arguments);
		
		return this;
	}
	
	load() {
		this.instance.load.apply(this, arguments);
		
		return this;
	}
	
	getLabelMap () {
		let me   = this,
			data = me.params.timeline.reduce(
				function(keys, key) {
					if (me.data[key]) {
						Object.keys(me.data[key]).forEach(function(key) {
							keys[key] = 1;
						});
					}
				
					return keys;
				},
				{}
			);
		
		if (me.params.trendLineKey) {
			data[me.getTrendKey()] = 1;
		}
		
		return data;
	}
	
	getTrendKey() {
		return this.params.trendLineKey + '_Trend';
	}
	
	prepareData(): any {
		let me          = this,
			data        = merge(isArray(me.data) ? [] : {}, me.data),
			x           = ['x'],
			timeline    = merge(isArray(me.params.timeline) ? [] : {}, me.params.timeline),
			columns     = [];
		
		
		if (Object.keys(timeline).length == 0) return [];
		
		let timeline2:any = timeline;
		
		timeline2 = timeline2
			.map(function(timestamp) { return parseInt(timestamp, 10); })
			.sort(function (a, b) { return a - b; });
		
		
		Object.keys(me.params.keysMap).forEach(function (key) {
			columns[key] = [];
		});
		
		timeline2.forEach(function(timestamp, i) {
			timestamp = parseInt(timestamp, 10);
			
			x.push(timestamp);
			
			Object.keys(columns).forEach(function (key) {
				if (data[timestamp]) {
					columns[key][i] = (data[timestamp][key] || 0);
				}
			});
		});
		timeline = timeline2;
		
		let resultData = Object.keys(columns).map(function(key) {
			return [key].concat(columns[key]);
		});
		
		resultData.unshift(x);
		
		return resultData;
	}
	
	getTimeline(stats) {
		let me         = this,
			borderTime = moment('02-25-2015', 'MM-DD-YYYY').toDate().getTime();
			
		if (isArray(stats)) {
			if (me.params.period == 'day' || me.params.period == 'summary') {
				return stats.reduce(function(arr, point) {
					if (parseInt(point.timestamp, 10) >= borderTime) arr.push(point.timestamp);
					
					return arr;
				}, []);
			} else {
				return stats.map(function (point) {
					return point.timestamp;
				});
			}
		} else {
			return Object.keys(stats);
		}
	}
}
